import appConfig from '@/features/app/constants/app-config';
import useCanClientUseCookieTokenStorage from '@/features/auth/hooks/useCanClientUseCookieTokenStorage';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';
import { _clientNow, _unixToClientTime } from '@/shared/functions/timezone';
import Cookies from 'js-cookie';
import { useCallback } from 'react';

export default function useProcessLoginResponse() {
  const featureFlags = useFeatureFlags();
  const canClientUseCookieStorage = useCanClientUseCookieTokenStorage();

  return useCallback(
    (data: {
      accessToken?: string;
      accessTokenExpiresIn?: number;
      accessTokenExpiresAt?: number;
      refreshToken?: string;
      refreshTokenExpiresAt?: number;
      refreshTokenExpiresIn?: number;
      rememberMe: boolean;
    }) => {
      const currentTime = _clientNow();

      if (featureFlags.auth.useCookieTokenStorage && data.accessTokenExpiresAt) {
        const accessTokenExpiresAt = _unixToClientTime(data.accessTokenExpiresAt);
        const refreshTokenExpiresAt =
          featureFlags.auth.enableRefreshToken && data.refreshTokenExpiresAt
            ? _unixToClientTime(data.refreshTokenExpiresAt)
            : undefined;

        // store the expires date in local cookie
        Cookies.set(appConfig.accessTokenExpireDateKey, data.accessTokenExpiresAt.toString(), {
          expires: data.rememberMe ? accessTokenExpiresAt.toDate() : undefined,
        });

        Cookies.set(appConfig.rememberMeKey, data.rememberMe.toString(), {
          expires: data.rememberMe
            ? refreshTokenExpiresAt
              ? refreshTokenExpiresAt.toDate()
              : accessTokenExpiresAt.toDate()
            : undefined,
        });

        if (featureFlags.auth.enableRefreshToken && data.refreshTokenExpiresAt) {
          Cookies.set(appConfig.refreshTokenExpireDateKey, data.refreshTokenExpiresAt.toString(), {
            expires: data.rememberMe
              ? _unixToClientTime(data.refreshTokenExpiresAt).toDate()
              : undefined,
          });
        }
      }

      if (
        !canClientUseCookieStorage &&
        featureFlags.auth.useLegacyTokenStorage &&
        data.accessToken &&
        data.accessTokenExpiresIn
      ) {
        const expiresAt = currentTime.add(data.accessTokenExpiresIn, 'second');
        const refreshTokenExpiresAt =
          featureFlags.auth.enableRefreshToken && data.refreshTokenExpiresIn
            ? currentTime.add(data.refreshTokenExpiresIn, 'second')
            : undefined;

        // store the token in the regular cookie if legacy token storage is enabled and cannot use cookie storage
        // which means new login will use new cookie storage
        Cookies.set(
          appConfig.authTokenKey,
          JSON.stringify({
            value: data.accessToken,
            expiresAt: expiresAt.unix(),
            refreshToken: featureFlags.auth.enableRefreshToken ? data.refreshToken : undefined,
            rememberMe: data.rememberMe,
            refreshTokenExpiresAt: featureFlags.auth.enableRefreshToken
              ? refreshTokenExpiresAt?.unix()
              : undefined,
          }),
          {
            expires: data.rememberMe ? expiresAt.toDate() : undefined,
          }
        );
      }

      return !canClientUseCookieStorage &&
        featureFlags.auth.useLegacyTokenStorage &&
        data.accessToken &&
        data.accessTokenExpiresIn
        ? {
            value: data.accessToken,
            expiresAt: currentTime.add(data.accessTokenExpiresIn, 'second').unix(),
            refreshToken: featureFlags.auth.enableRefreshToken ? data.refreshToken : undefined,
            rememberMe: data.rememberMe,
            refreshTokenExpiresAt:
              featureFlags.auth.enableRefreshToken && data.refreshTokenExpiresIn
                ? currentTime.add(data.refreshTokenExpiresIn, 'second').unix()
                : undefined,
          }
        : undefined;
    },
    [
      featureFlags.auth.useCookieTokenStorage,
      featureFlags.auth.useLegacyTokenStorage,
      featureFlags.auth.enableRefreshToken,
      canClientUseCookieStorage,
    ]
  );
}
